import { format, parseISO } from 'date-fns';

const monthMapping = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const minDate = new Date(0);

export function formatUtcToLocalDateTime(date) {
  if (!date) {
    return;
  }
  const x = new Date(date);
  if (x instanceof Date && !isNaN(x) && x > minDate) {
    return x.toLocaleString();
  }
  return '';
}

export function formatUTCDateToText(date) {
  if (!date) {
    return;
  }
  const x = new Date(date);
  if (x instanceof Date && !isNaN(x)) {
    return `${monthMapping[x.getUTCMonth()]} ${x.getUTCDate()}, ${x.getUTCFullYear()}`;
  }
  return '';
}

export function getYearsBetween(date1, date2) {
  if (!date1 || !date2) {
    return;
  }
  const x = new Date(date1);
  const y = new Date(date2);
  if (x instanceof Date && !isNaN(x) && y instanceof Date && !isNaN(y)) {
    // https://stackoverflow.com/questions/4060004/calculate-age-given-the-birth-date-in-the-format-yyyymmdd
    let age = date2.getFullYear() - date1.getFullYear();
    const m = date2.getMonth() - date1.getMonth();
    if (m < 0 || (m === 0 && date2.getDate() < date1.getDate())) {
      age--;
    }
    return age;
  }
}

export function formatDateShortUTC(date) {
  if (date) {
    const x = new Date(date);
    if (isNaN(x.getYear())) return null;
    return x.toISOString().slice(0, 10);
  }
}

export function formatDateShortLocal(date) {
  if (date) {
    const x = new Date(date);
    if (isNaN(x.getYear())) return null;
    const year = x.getFullYear();
    const month = String(x.getMonth() + 1).padStart(2, '0');
    const day = String(x.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

export function formatDateToStart(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString + 'T00:00:00');
  return date;
}

export function formatDateToEnd(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString + 'T23:59:59.999');
  return date;
}

export const decrementedDayIso = date => {
  if (!date) return '';

  const newDate = new Date(date);
  const day = newDate.getDate() - 1;
  const month = newDate.getMonth();
  const year = newDate.getFullYear();

  return new Date(year, month, day);
};

export const formatDate = (date, dateFormat = 'yyyy-MM-dd HH:mm') =>
  !date ? '' : format(new Date(date), dateFormat);

// Subscription start/end are calendar dates, not instants. A date picked at local midnight
// serializes to the previous UTC day for anyone ahead of UTC, so these two pin the value to
// UTC midnight on the way out and read the calendar date straight back off the ISO string on
// the way in - otherwise the day an admin picks is not the day another admin sees.
export const toUtcDateOnly = date => {
  if (!date) {
    return null;
  }

  const parsed = date instanceof Date ? date : new Date(date);
  if (isNaN(parsed)) {
    return null;
  }

  return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
};

export const utcDateOnlyPart = date => {
  if (!date) {
    return null;
  }

  if (date instanceof Date) {
    return isNaN(date) ? null : date.toISOString().slice(0, 10);
  }

  return String(date).slice(0, 10);
};

export const formatUtcDate = (date, dateFormat = 'yyyy-MM-dd', fallback = '') => {
  const datePart = utcDateOnlyPart(date);

  return datePart ? format(parseISO(datePart), dateFormat) : fallback;
};

export const formatRelativeDays = date => {
  if (!date) {
    return 'unknown';
  }

  const parsedDate = new Date(date);
  const daysAgo = Math.max(
    0,
    Math.floor((Date.now() - parsedDate.getTime()) / (24 * 60 * 60 * 1000)),
  );

  return `${format(parsedDate, 'MMM d, yyyy')} (${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago)`;
};
